import React, { useState } from 'react'
import { Avatar, Box, Button, Card, Divider, Group, LoadingOverlay, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { IconFile, IconLibrary, IconTableExport, IconUpload } from '@tabler/icons-react'
import Dropzone from 'react-dropzone'
import { MatchStrategy } from '../../../GlobalJobMatchingVersioning/types/versions'
import { useUploadMatchPriorities, usePricingVersionCache, useUpdatePricingVersion, useDownloadFileUploadErrors } from '../../../../../hooks/global-job-matching'
import { useParams } from 'react-router'
import { useDisclosure } from '@mantine/hooks'
import { MatchPrioritiesModal } from './MatchPrioritiesModal'
import { useAppState } from '../../../../../hooks/redux'
import { CardSelect } from '../../../../../components/common'
import { COLORS } from '../../../../../lib/mantineTheme'

export const Define = ({ debugEnabled }: { debugEnabled: boolean }) => {
  const params = useParams<{ id: string}>()
  const version = usePricingVersionCache(params.id)
  const [uploadErrors, setUploadErrors] = useState<{ fileName: string, rowCount: number, rows: any[] } | null>(null)
  const { session: { organization: { code }}} = useAppState()
  const { mutate: uploadFile, isLoading: uploadPending } = useUploadMatchPriorities(version?.id, setUploadErrors)
  const { mutate: downloadUploadErrors } = useDownloadFileUploadErrors()
  const { mutate: updateVersion } = useUpdatePricingVersion(params.id)
  const [matchPrioritiesModalOpened, { open: openPrioritiesModal, close: closePrioritiesModal }] = useDisclosure(false)
  const downloadTemplateUrl = `/${code}/merit-${code}/merit-admin/gen-upload-file?file_name=job_matching_matches_sample.csv&source_table=mp_upload_org_job_survey_job_map_columns`

  const matchStrategy = version?.matchStrategy
  const handleMatchStrategySelect = (selection: MatchStrategy) => {
    if (selection === matchStrategy) return
    updateVersion({ matchStrategy: selection })
  }

  return (
    <Stack p='xl'>
      <MatchPrioritiesModal opened={matchPrioritiesModalOpened} onClose={closePrioritiesModal} />
      <Group wrap='nowrap' gap='xl' align='stretch'>
        <CardSelect
          flex={1}
          p='xl'
          isEnabled={matchStrategy === 'current'}
          onClick={() => handleMatchStrategySelect(MatchStrategy.CURRENT)}
        >
          <Stack align='center' gap={0}>
            <Avatar my='lg' radius='50%' size={170} color={matchStrategy === 'current' ? 'blue' : 'gray'}>
              <IconLibrary size={40} />
            </Avatar>
            <Title order={3} mb='sm'>Use Current Matches</Title>
            <Text ta='center' size='lg'>The matches in Market Price A Job.</Text>
          </Stack>
        </CardSelect>
        <CardSelect
          flex={1}
          p='xl'
          isEnabled={matchStrategy === 'file'}
          onClick={() => handleMatchStrategySelect(MatchStrategy.FILE)}
        >
          <Stack align='center' gap={0}>
            <Avatar my='lg' radius='50%' size={170} color={matchStrategy === 'file' ? 'blue' : 'gray'}>
              <IconTableExport size={40} />
            </Avatar>
            <Title order={3} mb='sm'>Use Match Priorities</Title>
            <Text ta='center' size='lg'>Your match hierarchy or decision tree by job with priorities defined by match.</Text>
            <Text size='lg'>The system will find best available matches.</Text>
          </Stack>
        </CardSelect>
      </Group>
      { matchStrategy === 'file' &&
      <Stack>
        <Divider size='sm' my='xl' />
        <Title>Upload match priorities file</Title>
        <Group>
          <Text size='lg'>File must be in .csv format.</Text>
          <UnstyledButton c='blue' fw='600' component='a' href={downloadTemplateUrl}>Download Template</UnstyledButton>
        </Group>
        { !version.metadata?.filename ?
        <Stack align='center'>
          <Dropzone
            onDropAccepted={files => uploadFile({ versionId: params?.id, file: files[0] })}
            multiple={false}
            accept={'.csv'}
            maxSize={25 * 1048576}
          >
            {
              ({ getRootProps, getInputProps }) => (
                <Card withBorder {...getRootProps()} w='100%' style={{ border: '1px dashed #d7e0e7', background: 'rgba(247, 248, 249, 0.59)'}} py={100}>
                  <LoadingOverlay visible={uploadPending} />
                  <Stack align='center'>
                    <IconUpload />
                    <Title order={3}>Drag .csv here to replace</Title>
                    <Text style={{ fontSize: 13 }}>or click to browse</Text>
                    <input {...getInputProps()} />
                  </Stack>
                </Card>
              )
            }
          </Dropzone>
        </Stack> :
        <Card withBorder>
          <Group justify='space-between'>
            <Group onClick={debugEnabled ? openPrioritiesModal : () => undefined}>
              <IconFile size={30} color={COLORS.BODY_SECONDARY} />
              <Title order={5} style={{ fontSize: 13, fontWeight: 600 }}>{version.metadata?.filename}</Title>
            </Group>
            <Dropzone
              onDropAccepted={files => uploadFile({ versionId: params?.id, file: files[0] })}
              multiple={false}
              accept={'.csv'}
              maxSize={25 * 1048576}
            >
              {
                ({ getRootProps, getInputProps }) => (
                  <Box {...getRootProps()}>
                    <Stack align='center'>
                      <Button disabled={uploadPending} loading={uploadPending} color='red'>Delete & Replace</Button>
                      <input {...getInputProps()} />
                    </Stack>
                  </Box>
                )
              }
            </Dropzone>
          </Group>
        </Card>
        }
      </Stack>
      }
      {uploadErrors && matchStrategy === 'file' &&
        <Card my='xl' py='xl' style={{ borderColor: 'red' }}>
          <Stack align='center'>
            <Title order={3}>Sorry we are not able to upload "{uploadErrors.fileName}".</Title>
            { uploadErrors.rowCount &&
            <Text>There are {uploadErrors.rowCount} errors. Download error file <Text c='blue' fw='500' span style={{ cursor: 'pointer' }} onClick={() => downloadUploadErrors(uploadErrors.rows)}>here</Text></Text>
            }
            <Text>Please fix the errors and try again or select a different file.</Text>
          </Stack>
        </Card>
      }
    </Stack>
  )
}
